// Users Console — Palantir-style dark/light console with interactive roster,
// per-user inspector drawer, sortable/searchable table and CSV export.
import React, { useState, useEffect } from 'react';
import { ConsoleShell, KPI, Panel, SectionLabel, Bar, StatLine, Empty, Pill, Table, Drawer, ExportButton, StackedArea, Funnel, PALETTE, useConsoleTheme, useIsMobile } from '../components/Console';
import { usePassesStats, useSanaStats, useSearchStats, useSupportStats } from '../hooks/useAnalytics';
import { supabase } from '../supabaseClient';
import { takeFocus, setFocus } from '../utils/consoleBus';

const fmt = n => (n == null ? '—' : Number(n).toLocaleString());
const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);
const day = d => (d ? new Date(d).toISOString().slice(0, 10) : null);
const ago = ts => {
  if (!ts) return 'never';
  const s = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return Math.floor(s / 60) + 'm ago';
  if (s < 86400) return Math.floor(s / 3600) + 'h ago';
  return Math.floor(s / 86400) + 'd ago';
};

// Live presence — admin_live_users (5-min window), refreshed by realtime
// member_presence changes (fed by the app heartbeat) + 60s poll fallback.
function useLiveUsers() {
  const [d, setD] = useState(null);
  useEffect(() => {
    let c = false;
    const load = () => supabase.rpc('admin_live_users').then(({ data }) => { if (!c) setD(data || []); });
    load();
    const iv = setInterval(load, 60000);
    const ch = supabase.channel('admin-live-users')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'member_presence' }, load)
      .subscribe();
    return () => { c = true; clearInterval(iv); supabase.removeChannel(ch); };
  }, []);
  return d;
}

function useAuthUsers() {
  const [d, setD] = useState(null);
  useEffect(() => { let c = false; supabase.rpc('admin_get_auth_users').then(({ data }) => { if (!c) setD(data || []); }); return () => { c = true; }; }, []);
  return d;
}
function useAbuse() {
  const [d, setD] = useState({ flagged: 0, locked: 0 });
  useEffect(() => {
    let c = false;
    supabase.rpc('admin_abuse_counts').then(({ data }) => { if (!c && data) setD({ flagged: data.flagged || 0, locked: data.locked || 0 }); });
    return () => { c = true; };
  }, []);
  return d;
}

function UserInspector({ user }) {
  const { t } = useConsoleTheme();
  const [d, setD] = useState(null);
  useEffect(() => {
    let c = false; setD(null);
    supabase.rpc('admin_user_detail', { p_user_id: user.id }).then(({ data }) => {
      if (c) return;
      const r = data || {};
      setD({
        passes: r.passes || [], turns: r.sana_turns || 0, invocations: r.sana_invocations || 0,
        flagged: !!r.flagged, locked: !!r.locked,
        activePass: r.active_plan ? { pass_type: r.active_plan, expires_at: r.active_expires } : null,
      });
    });
    return () => { c = true; };
  }, [user]);
  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
        <div style={{ width:40, height:40, borderRadius:'50%', background:t.seal, color:t.sealc, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:500 }}>
          {(user.email || '?').slice(0, 2).toUpperCase()}
        </div>
        <div style={{ minWidth:0 }}>
          <div style={{ fontSize:'.8rem', color:t.tx, fontWeight:500, overflow:'hidden', textOverflow:'ellipsis' }}>{user.email || '\u2014'}</div>
          <div style={{ fontSize:'.64rem', color:t.mut, fontFamily:'monospace' }}>{(user.id || '').slice(0, 18)}…</div>
        </div>
      </div>
      <StatLine label="Joined" value={day(user.created_at) || '\u2014'} />
      <StatLine label="Last sign-in" value={day(user.last_sign_in_at) || 'never'} />
      {!d ? <div style={{ marginTop:10 }}><Empty>Loading profile…</Empty></div> : (
        <>
          <StatLine label="Plan" value={d.activePass ? d.activePass.pass_type : 'free'} color={d.activePass ? t.up : t.mut} />
          {d.activePass && d.activePass.expires_at && <StatLine label="Renews / expires" value={day(d.activePass.expires_at)} />}
          <StatLine label="Passes (all)" value={fmt(d.passes.length)} />
          <StatLine label="Sana queries" value={fmt(d.invocations)} />
          <StatLine label="Sana turns" value={fmt(d.turns)} />
          <StatLine label="Flagged" value={d.flagged ? 'yes' : 'no'} color={d.flagged ? t.danger : t.up} />
          {d.locked && <StatLine label="Locked" value="yes" color={t.danger} />}
          <div style={{ height:1, background:t.bd, margin:'12px 0' }} />
          <div style={{ fontSize:'.66rem', color:t.mut, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:8 }}>Pass history</div>
          {d.passes.length === 0 ? <Empty>No passes.</Empty> : d.passes.slice(0, 10).map((p, i) => (
            <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:'.7rem', padding:'4px 0', borderBottom:`.5px solid ${t.bd}` }}>
              <span style={{ color:t.tx }}>{p.pass_type}</span>
              <span style={{ color:t.mut }}>{day(p.created_at)} → {day(p.expires_at) || '\u2014'}</span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function useGrowthSeries() {
  const [d, setD] = useState(null);
  useEffect(() => { let c = false; supabase.rpc('admin_growth_series').then(({ data }) => { if (!c) setD(data || []); }); return () => { c = true; }; }, []);
  return d;
}
function useJourney() {
  const [d, setD] = useState(null);
  useEffect(() => { let c = false; supabase.rpc('admin_user_journey').then(({ data }) => { if (!c) setD(data || {}); }); return () => { c = true; }; }, []);
  return d;
}
function ActivityTrail({ userId }) {
  const { t } = useConsoleTheme();
  const [ev, setEv] = useState(null);
  useEffect(() => {
    let x = false;
    supabase.from('user_events').select('event_type,event_data,occurred_at').eq('user_id', userId)
      .order('occurred_at', { ascending: false }).limit(30)
      .then(({ data }) => { if (!x) setEv(data || []); });
    return () => { x = true; };
  }, [userId]);
  const label = e => e.event_type === 'app_open' ? 'Opened the app'
    : e.event_type === 'screen_view' ? 'Viewed ' + ((e.event_data && e.event_data.screen) || 'screen')
    : e.event_type;
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: '0.78rem', fontWeight: 600, letterSpacing: '.14em', textTransform: 'uppercase', color: t.gold, marginBottom: 8 }}>Recent activity</div>
      {!ev ? <Empty>Loading…</Empty> : ev.length === 0 ? <Empty>No activity events yet — screen tracking lands with the next app build.</Empty> : (
        ev.map((e, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '5px 0', borderBottom: `1px solid ${t.bd}`, fontSize: '0.85rem' }}>
            <span style={{ color: t.tx }}>{label(e)}</span>
            <span style={{ color: t.mut, whiteSpace: 'nowrap' }}>{ago(e.occurred_at)}</span>
          </div>
        ))
      )}
    </div>
  );
}

function useConversionSources() {
  const [d, setD] = useState(null);
  useEffect(() => {
    let x = false;
    supabase.from('user_passes').select('purchase_source').limit(1000).then(({ data }) => {
      if (x) return;
      const m = { app: 0, web: 0, unattributed: 0 };
      (data || []).forEach(r => { const k = r.purchase_source === 'app' ? 'app' : r.purchase_source === 'web' ? 'web' : 'unattributed'; m[k] += 1; });
      setD(m);
    });
    return () => { x = true; };
  }, []);
  return d;
}

function Body({ onNav }) {
  const { t } = useConsoleTheme();
  const mobile = useIsMobile();
  const users = useAuthUsers();
  const passes = usePassesStats().data;
  const { data: sana } = useSanaStats();
  const { data: search } = useSearchStats();
  const { data: support } = useSupportStats();
  const abuse = useAbuse();
  const growth = useGrowthSeries();
  const journey = useJourney();
  const GKEYS = [{ key:'signups', label:'Signups', color:PALETTE[1] }, { key:'sana', label:'Sana', color:PALETTE[0] }, { key:'search', label:'Search', color:PALETTE[2] }, { key:'passes', label:'Passes', color:PALETTE[3] }];
  const [sel, setSel] = useState(null);
  const [focusUserId, setFocusUserId] = useState(() => { const f = takeFocus(['user']); return f ? f.value : null; });
  useEffect(() => { if (focusUserId && users) { const u = (users || []).find(x => x.id === focusUserId); if (u) setSel(u); setFocusUserId(null); } }, [users, focusUserId]);
  const convSrc = useConversionSources();
  const liveUsers = useLiveUsers();
  const liveNow = (liveUsers || []).filter(x => x.live);
  const presenceByUser = {};
  (liveUsers || []).forEach(x => { presenceByUser[x.user_id] = x; });
  const now = Date.now();
  const total = users ? users.length : null;
  const new7 = users ? users.filter(u => u.created_at && (now - new Date(u.created_at)) < 7 * 864e5).length : null;
  const active7 = users ? users.filter(u => u.last_sign_in_at && (now - new Date(u.last_sign_in_at)) < 7 * 864e5).length : null;
  const maxQ = Math.max(1, ...(search.topQueries || []).map(q => q.count));
  const maxC = Math.max(1, ...(search.topCountries || []).map(c => c.count));
  const col = n => (mobile ? (n >= 4 ? '1fr 1fr' : '1fr') : `repeat(${n},1fr)`);

  const rosterCols = [
    { label:'Email', key:'e', text:r => r.e },
    { label:'Joined', key:'j', muted:true, sort:r => r.jv, text:r => r.j },
    { label:'Last seen', muted:true, align:'right', sort:r => r.lv, text:r => r.l, render:r => r.live ? <span style={{ color:'#17a86a', fontWeight:600 }}>● live</span> : r.l },
  ];
  const rosterRows = (users || []).map(u => {
    const p = presenceByUser[u.id];
    const seen = (p && p.last_seen) || u.last_sign_in_at;
    return {
      e: u.email || '—', j: day(u.created_at) || '—',
      l: seen ? ago(seen) : 'never', live: !!(p && p.live),
      jv: u.created_at ? +new Date(u.created_at) : 0, lv: seen ? +new Date(seen) : 0,
      _u: u,
    };
  });

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns: col(6), gap:10 }}>
        <KPI label="Users total" value={fmt(total)} sub="registered" accent="#314F5C" />
        <KPI label="New · 7d" value={fmt(new7)} sub="sign-ups" subColor={t.up} accent="#28a745" />
        <KPI label="Active · 7d" value={fmt(active7)} sub="signed in" accent={t.accent} />
        <KPI label="Pass holders" value={fmt(passes.uniqueUsers)} sub={`${fmt(passes.active)} active`} accent="#819D84" />
        <KPI label="Sana queries" value={fmt(sana.total)} sub={`${sana.resultRate}% result`} accent="#5a8fa6" />
        <KPI label="Open tickets" value={fmt(support.open)} sub={support.open > 0 ? 'needs attention' : 'all clear'} subColor={support.open > 0 ? t.warn : t.up} accent={support.open > 0 ? '#e53935' : '#17a86a'} />
      </div>

      <SectionLabel right={liveUsers ? `${liveNow.length} live · 5-min window` : undefined}>Live now</SectionLabel>
      <Panel style={{ marginBottom: 4 }}>
        {!liveUsers ? <Empty>Loading presence…</Empty> : liveNow.length === 0 ? (
          <Empty>
            No one is in the app right now.
            {(() => { const last = (liveUsers || []).find(x => x.last_seen); return last ? ` Last activity ${ago(last.last_seen)} (${last.email || 'unknown'}).` : ' No presence recorded yet.'; })()}
          </Empty>
        ) : (
          <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
            {liveNow.map(x => (
              <span key={x.user_id} style={{ display:'inline-flex', alignItems:'center', gap:7, padding:'6px 12px', borderRadius:99, background:t.live, color:t.livec, fontSize:'0.85rem', fontWeight:600 }}>
                <span style={{ width:7, height:7, borderRadius:'50%', background:'#2da06a' }} />
                {x.email || x.user_id.slice(0, 8)} · {ago(x.last_seen)}
              </span>
            ))}
          </div>
        )}
      </Panel>

      <SectionLabel>Growth & conversion</SectionLabel>
      <div style={{ display:'grid', gridTemplateColumns: mobile ? '1fr' : '1.3fr 1fr', gap:12 }}>
        <Panel title="▵ 60-day activity" right="signups · sana · search · passes">
          {!growth ? <Empty>Loading trend…</Empty> : <StackedArea data={growth} keys={GKEYS} />}
        </Panel>
        <Panel title="⧗ Activation funnel" right="lifetime">
          {!journey ? <Empty>Loading funnel…</Empty> : <Funnel steps={journey.steps} />}
          {convSrc && (
            <div style={{ display:'flex', alignItems:'baseline', gap:10, flexWrap:'wrap', marginTop:12, paddingTop:10, borderTop:`1px solid ${t.bd}` }}>
              <span style={{ fontSize:'0.72rem', fontWeight:600, letterSpacing:'.14em', textTransform:'uppercase', color:t.gold }}>Purchased via</span>
              <span style={{ fontSize:'0.88rem', color:t.tx, fontWeight:600 }}>App {convSrc.app}</span>
              <span style={{ fontSize:'0.88rem', color:t.tx, fontWeight:600 }}>· Web {convSrc.web}</span>
              {convSrc.unattributed > 0 && <span style={{ fontSize:'0.82rem', color:t.mut }}>· {convSrc.unattributed} unattributed (pre-attribution)</span>}
            </div>
          )}
        </Panel>
      </div>

      <SectionLabel>Engagement</SectionLabel>
      <div style={{ display:'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap:12 }}>
        <Panel title="◔ Top searches">
          {(search.topQueries || []).length === 0 ? <Empty>No searches recorded yet.</Empty>
            : (search.topQueries || []).slice(0, 8).map(q => <Bar key={q.query} label={q.query} value={fmt(q.count)} pct={Math.round(q.count / maxQ * 100)} labelWidth={150} hint={`${q.query} — ${fmt(q.count)} searches`} />)}
        </Panel>
        <Panel title="◷ Searches by country">
          {(search.topCountries || []).length === 0 ? <Empty>No country data yet.</Empty>
            : (search.topCountries || []).slice(0, 6).map(c => <Bar key={c.country} label={c.country} value={fmt(c.count)} pct={Math.round(c.count / maxC * 100)} labelWidth={120} hint={`${c.country} — ${fmt(c.count)} searches`} />)}
          <div style={{ height:1, background:t.bd, margin:'10px 0' }} />
          <StatLine label="Searches total" value={fmt(search.total)} />
          <StatLine label="Zero-result rate" value={`${search.zeroResultRate || 0}%`} color={(search.zeroResultRate || 0) > 30 ? t.warn : t.tx} />
          <StatLine label="Sana today" value={fmt(sana.todayCount)} />
        </Panel>
      </div>

      <SectionLabel>Safety</SectionLabel>
      <div style={{ display:'grid', gridTemplateColumns: col(3), gap:10 }}>
        <KPI label="Flagged accounts" value={fmt(abuse.flagged)} sub={abuse.flagged > 0 ? 'review' : 'all clear'} subColor={abuse.flagged > 0 ? t.warn : t.up} accent={abuse.flagged > 0 ? '#dc3545' : '#17a86a'} />
        <KPI label="Locked accounts" value={fmt(abuse.locked)} sub={abuse.locked > 0 ? 'suspended' : 'none'} accent={abuse.locked > 0 ? '#d97706' : '#17a86a'} />
        <KPI label="Pass conversion" value={total ? `${pct(passes.uniqueUsers, total)}%` : '—'} sub="users → paid" accent="#9F7E4F" />
      </div>

      <SectionLabel right="click a row to inspect">User roster</SectionLabel>
      <Panel title="◈ Registered users" right={<ExportButton filename="sanoire-users" cols={rosterCols} rows={rosterRows} />}>
        <Table empty="No users yet." onRowClick={r => setSel(r._u)} cols={rosterCols} rows={rosterRows} />
      </Panel>

      <Drawer open={!!sel} onClose={() => setSel(null)} title="User inspector">
        {sel && <UserInspector user={sel} />}
        {sel && <ActivityTrail userId={sel.id} />}
        {sel && (
          <button
            onClick={() => { setFocus({ type: 'userlookup', value: sel.id }); setSel(null); try { window.dispatchEvent(new CustomEvent('sanoire:hubtab', { detail: { hub: 'Users', tab: 'lookup' } })); } catch (_e) { if (onNav) onNav('userdetail'); } }}
            style={{ marginTop: 14, width: '100%', padding: '9px 14px', borderRadius: 9, border: '1px solid rgba(159,126,79,0.45)', background: 'transparent', color: t.gold, fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}>
            Full profile & activity report (PDF) →
          </button>
        )}
      </Drawer>
    </div>
  );
}

export default function UsersConsole(props) {
  return <ConsoleShell title="Users Console"><Body onNav={props.onNav} /></ConsoleShell>;
}
